<template>
    <div class="mqtt-lag-indicator px-3 py-2 font-mono">
        <div
            v-tooltip.right="!sidebarOpen ? `MQTT ${mqttDisplay}` : null"
            class="flex justify-content-between gap-2"
            :class="{ 'justify-content-center': !sidebarOpen }">
            <span v-if="sidebarOpen" class="lag-label">MQTT</span>
            <span :class="getLagColor(mqttLagMs)">{{ mqttDisplay || "—" }}</span>
        </div>
        <div
            v-if="mobileUserTimestamp > 0"
            v-tooltip.right="!sidebarOpen ? `Edge User ${e2eDisplay}` : null"
            class="flex justify-content-between gap-2"
            :class="{ 'justify-content-center': !sidebarOpen }">
            <span v-if="sidebarOpen" class="lag-label">Edge User</span>
            <span :class="getLagColor(e2eLagMs)">{{ e2eDisplay }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useStreamService } from "@/composables/useStreamService";
import { useInterfaceProperties } from "@/composables/useInterfaceProperties.js";

const streamService = useStreamService();
const { sidebarOpen } = useInterfaceProperties();

const mqttServerTimestamp = computed(
    () => streamService.mqttServerTimestamp.value,
);
const mobileUserTimestamp = computed(
    () => streamService.mobileUserTimestamp.value,
);

const now = ref(Date.now());
let timer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
    timer = setInterval(() => {
        now.value = Date.now();
    }, 500);
});
onBeforeUnmount(() => {
    if (timer) clearInterval(timer);
});

const mqttLagMs = computed(() =>
    mqttServerTimestamp.value
        ? Math.max(0, now.value - mqttServerTimestamp.value)
        : -1,
);
const e2eLagMs = computed(() =>
    mobileUserTimestamp.value
        ? Math.max(0, now.value - mobileUserTimestamp.value)
        : -1,
);

const formatLag = (ms: number) => {
    if (ms < 0) return "";
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
};

const mqttDisplay = computed(() => formatLag(mqttLagMs.value));
const e2eDisplay = computed(() => formatLag(e2eLagMs.value));

const getLagColor = (ms: number) => {
    if (ms < 0) return "lag-idle";
    if (ms <= 2000) return "lag-ok";
    if (ms <= 10000) return "lag-warn";
    return "lag-crit";
};
</script>

<style scoped>
.mqtt-lag-indicator {
    font-size: 12px;
    font-weight: 600;
    line-height: 1.5;
    letter-spacing: 0.2px;
    background: rgba(0, 0, 0, 0.35);
    border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.lag-label {
    color: #fff;
    opacity: 0.75;
    font-weight: 500;
}
.lag-idle {
    color: #fff;
    opacity: 0.5;
}
.lag-ok {
    color: #4ade80;
}
.lag-warn {
    color: #facc15;
}
.lag-crit {
    color: #f87171;
}
</style>
